import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Info, RefreshCw, CreditCard } from 'lucide-react';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '@/components/ui/table';
import { FeeBreakdownRow } from './FeeBreakdownRow';
import { EnabledBadge } from './StatusBadge';
import { resolveCustomFeeLabel } from './custom-fee-display';
import { formatCurrency } from './utils';
import { formatMoney } from '@/lib/money';

interface CustomFeeItem {
  id: string;
  description: string;
  amount: number;
  isRecurring: boolean;
}

interface ActivationCondition {
  minApprovedTxns: number;
  currentApprovedTxns: number;
  met: boolean;
}

interface DomainActivationCondition {
  requiresFirstSale: boolean;
  met: boolean;
}

interface DomainFee {
  activationCondition?: DomainActivationCondition | null;
}

interface WeeklyBaseFee {
  count: number;
  unitPrice: number;
  total: number;
  enabled: boolean;
  activationCondition?: ActivationCondition | null;
}

function weeklyBaseFeeSubtitle(
  weeklyBaseFee: WeeklyBaseFee | undefined,
  t: TFunction<'billing'>
): string | null {
  const cond = weeklyBaseFee?.activationCondition;
  if (!cond) return null;
  if (cond.met) {
    return t('feeBreakdown.activation.activated', { count: cond.currentApprovedTxns });
  }
  return t('feeBreakdown.activation.pending', {
    count: cond.minApprovedTxns,
    current: cond.currentApprovedTxns,
  });
}

function domainFeeSubtitle(fee: DomainFee | undefined, t: TFunction<'billing'>): string | null {
  const cond = fee?.activationCondition;
  if (!cond || cond.met) return null;
  return t('feeBreakdown.activation.pendingFirstSale');
}

interface FeeBreakdown {
  weeklyBaseFee?: WeeklyBaseFee;
  approvedTransactions?: any;
  subclubTransactions?: any;
  declinedTransactions?: any;
  refunds?: any;
  chargebackAlerts?: any;
  threeDsVerifications?: any;
  latePayments?: any;
  automaticRetries?: any;
  upsells?: any;
  subscriptions?: any;
  applePay?: any;
  googlePay?: any;
  applePayDomains?: any;
  customDomains?: any;
  /** Tagada Rx: fee collected at payment, `total` is 0 (not in the invoice total). */
  rxTransactions?: any;
  customFees?: {
    count: number;
    items: CustomFeeItem[];
    total: number;
    enabled: boolean;
  };
  platformCredits?: {
    enabled?: boolean;
    total?: number;
    count?: number;
  };
}

interface FeeBreakdownTableProps {
  breakdown: FeeBreakdown;
  totalFees: number;
}

export function FeeBreakdownTable({ breakdown, totalFees }: FeeBreakdownTableProps) {
  const { t } = useTranslation('billing');

  const calculateTotalFixed = () => {
    return [
      (breakdown.approvedTransactions?.unitPrice || 0) * (breakdown.approvedTransactions?.count || 0),
      (breakdown.subclubTransactions?.unitPrice || 0) * (breakdown.subclubTransactions?.count || 0),
      (breakdown.declinedTransactions?.unitPrice || 0) * (breakdown.declinedTransactions?.count || 0),
      (breakdown.refunds?.unitPrice || 0) * (breakdown.refunds?.count || 0),
      (breakdown.chargebackAlerts?.unitPrice || 0) * (breakdown.chargebackAlerts?.count || 0),
      (breakdown.threeDsVerifications?.unitPrice || 0) * (breakdown.threeDsVerifications?.count || 0),
      (breakdown.latePayments?.unitPrice || 0) * (breakdown.latePayments?.count || 0),
      breakdown.applePayDomains?.total || 0,
      breakdown.customDomains?.total || 0,
      breakdown.weeklyBaseFee?.total || 0,
      breakdown.customFees?.total || 0
    ].reduce((sum, val) => sum + val, 0) / 100;
  };

  const calculateTotalPercentage = () => {
    return [
      breakdown.approvedTransactions?.percentageTotal || 0,
      breakdown.subclubTransactions?.percentageTotal || 0,
      breakdown.declinedTransactions?.percentageTotal || 0,
      breakdown.refunds?.percentageTotal || 0,
      breakdown.chargebackAlerts?.percentageTotal || 0,
      breakdown.threeDsVerifications?.percentageTotal || 0,
      breakdown.latePayments?.percentageTotal || 0,
      breakdown.automaticRetries?.percentageTotal || 0,
      breakdown.upsells?.percentageTotal || 0,
      breakdown.subscriptions?.percentageTotal || 0,
      breakdown.applePay?.percentageTotal || 0,
      breakdown.googlePay?.percentageTotal || 0
    ].reduce((sum, val) => sum + val, 0) / 100;
  };

  const perWeek = t('feeBreakdown.suffixes.perWeek');
  const walletTag = t('feeBreakdown.tags.walletPayment');
  const weeklyFeeTag = t('feeBreakdown.tags.weeklyFee');
  const revenueBoosterTag = t('feeBreakdown.tags.revenueBooster');

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t('feeBreakdown.title')}</h3>
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          <Info className="h-4 w-4" />
          <span>{t('feeBreakdown.formulaHint')}</span>
        </div>
      </div>
      <div className="bg-background overflow-hidden rounded-lg border">
        <Table>
          <TableHeader className="bg-muted">
            <TableRow className="border-b">
              <TableHead className="px-4 py-3 text-left font-medium">{t('feeBreakdown.columns.transactionType')}</TableHead>
              <TableHead className="px-2 py-3 text-right font-medium">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.count')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.countHint')}</span>
                </div>
              </TableHead>
              <TableHead className="px-2 py-3 text-right font-medium">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.volume')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.volumeHint')}</span>
                </div>
              </TableHead>
              <TableHead className="border-l-2 border-blue-100 px-2 py-3 text-right font-medium text-blue-700">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.fixedRate')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.fixedRateHint')}</span>
                </div>
              </TableHead>
              <TableHead className="px-2 py-3 text-right font-medium text-blue-700">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.percentageRate')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.percentageRateHint')}</span>
                </div>
              </TableHead>
              <TableHead className="border-l-2 border-green-100 px-2 py-3 text-right font-medium text-green-700">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.fixedFees')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.fixedFeesHint')}</span>
                </div>
              </TableHead>
              <TableHead className="px-2 py-3 text-right font-medium text-green-700">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.percentageFees')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.percentageFeesHint')}</span>
                </div>
              </TableHead>
              <TableHead className="border-l-2 border-gray-200 px-2 py-3 text-right font-bold">
                <div className="flex flex-col items-end">
                  <span>{t('feeBreakdown.columns.totalFee')}</span>
                  <span className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.columns.totalFeeHint')}</span>
                </div>
              </TableHead>
              <TableHead className="px-2 py-3 text-center font-medium">{t('feeBreakdown.columns.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {breakdown.weeklyBaseFee && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.weeklyBaseFee')}
                icon={<span className="text-lg">💼</span>}
                categoryTag={t('feeBreakdown.tags.platformFee')}
                item={breakdown.weeklyBaseFee}
                className="hover:bg-purple-50/30 border-l-4 border-purple-400 bg-purple-50/20"
                unitPriceSuffix={perWeek}
                subtitle={weeklyBaseFeeSubtitle(breakdown.weeklyBaseFee, t)}
              />
            )}

            {breakdown.approvedTransactions && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.approvedTransactions')}
                icon={<div className="h-2 w-2 rounded-full bg-green-500"></div>}
                item={breakdown.approvedTransactions}
              />
            )}

            {breakdown.subclubTransactions && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.subclubTransactions')}
                icon={<div className="h-2 w-2 rounded-full bg-blue-500"></div>}
                item={breakdown.subclubTransactions}
                className="border-b bg-blue-50/30 hover:bg-blue-50/50"
              />
            )}

            {breakdown.declinedTransactions && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.declinedTransactions')}
                icon={<div className="h-2 w-2 rounded-full bg-amber-500"></div>}
                item={breakdown.declinedTransactions}
              />
            )}

            {breakdown.refunds && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.refundProcessing')}
                icon={<div className="h-2 w-2 rounded-full bg-red-500"></div>}
                item={breakdown.refunds}
              />
            )}

            {breakdown.chargebackAlerts && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.chargebackAlerts')}
                icon={<div className="h-2 w-2 rounded-full bg-orange-500"></div>}
                item={breakdown.chargebackAlerts}
              />
            )}

            {breakdown.threeDsVerifications && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.threeDsVerifications')}
                icon={<div className="h-2 w-2 rounded-full bg-purple-500"></div>}
                item={breakdown.threeDsVerifications}
              />
            )}

            {breakdown.latePayments && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.latePayments')}
                icon={<div className="h-2 w-2 rounded-full bg-yellow-500"></div>}
                item={breakdown.latePayments}
              />
            )}

            {breakdown.automaticRetries && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.automaticRetries')}
                icon={<RefreshCw className="h-4 w-4 text-orange-600" />}
                categoryTag={t('feeBreakdown.tags.growthAddOn')}
                item={breakdown.automaticRetries}
                className="hover:bg-orange-50/30 border-l-4 border-orange-200 bg-orange-50/20"
                showUnitPrice={false}
              />
            )}

            {breakdown.upsells && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.orderBumps')}
                icon={<CreditCard className="h-4 w-4 text-purple-600" />}
                categoryTag={revenueBoosterTag}
                item={breakdown.upsells}
                className="hover:bg-purple-50/30 border-l-4 border-purple-200 bg-purple-50/20"
                showUnitPrice={false}
              />
            )}

            {breakdown.subscriptions && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.subscriptions')}
                icon={<RefreshCw className="h-4 w-4 text-blue-600" />}
                categoryTag={revenueBoosterTag}
                item={breakdown.subscriptions}
                className="hover:bg-blue-50/30 border-l-4 border-blue-200 bg-blue-50/20"
                showUnitPrice={false}
              />
            )}

            {breakdown.applePay && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.applePay')}
                icon={<span className="text-lg">🍎</span>}
                categoryTag={walletTag}
                item={breakdown.applePay}
                className="hover:bg-gray-50/30 border-l-4 border-gray-400 bg-gray-50/20"
                showUnitPrice={false}
              />
            )}

            {breakdown.googlePay && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.googlePay')}
                icon={<span className="text-lg">💳</span>}
                categoryTag={walletTag}
                item={breakdown.googlePay}
                className="hover:bg-green-50/30 border-l-4 border-green-400 bg-green-50/20"
                showUnitPrice={false}
              />
            )}

            {breakdown.rxTransactions && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.rxTransactions')}
                icon={<span className="text-lg">💊</span>}
                categoryTag={t('feeBreakdown.tags.collectedAtPayment')}
                item={{
                  ...breakdown.rxTransactions,
                  percentageTotal:
                    breakdown.rxTransactions.collectedTotal ?? breakdown.rxTransactions.percentageTotal,
                  total: 0,
                }}
                className="hover:bg-emerald-50/30 border-l-4 border-emerald-400 bg-emerald-50/20"
                showUnitPrice={false}
                subtitle={t('feeBreakdown.activation.rxCollectedAtPayment')}
              />
            )}

            {breakdown.applePayDomains && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.applePayDomains')}
                icon={<span className="text-lg">🌐</span>}
                categoryTag={weeklyFeeTag}
                item={breakdown.applePayDomains}
                className="hover:bg-indigo-50/30 border-l-4 border-indigo-400 bg-indigo-50/20"
                unitPriceSuffix={perWeek}
                subtitle={domainFeeSubtitle(breakdown.applePayDomains, t)}
              />
            )}

            {breakdown.customDomains && (
              <FeeBreakdownRow
                label={t('feeBreakdown.rows.customDomains')}
                icon={<span className="text-lg">🔗</span>}
                categoryTag={weeklyFeeTag}
                item={breakdown.customDomains}
                className="hover:bg-cyan-50/30 border-l-4 border-cyan-400 bg-cyan-50/20"
                unitPriceSuffix={perWeek}
                subtitle={domainFeeSubtitle(breakdown.customDomains, t)}
              />
            )}

            {breakdown.customFees?.enabled && breakdown.customFees.items.map((fee) => (
              <TableRow
                key={fee.id}
                className="hover:bg-amber-50/30 border-l-4 border-amber-400 bg-amber-50/20"
              >
                <TableCell className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💰</span>
                    <span className="font-medium">{resolveCustomFeeLabel(fee.description, t)}</span>
                    <span className="text-muted-foreground text-xs">
                      {fee.isRecurring ? t('feeBreakdown.tags.recurring') : t('feeBreakdown.tags.oneTime')}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground px-2 text-right font-medium">-</TableCell>
                <TableCell className="text-muted-foreground px-2 text-right font-medium">-</TableCell>
                <TableCell className="text-muted-foreground border-l-2 border-blue-100 px-2 text-right">-</TableCell>
                <TableCell className="text-muted-foreground px-2 text-right">-</TableCell>
                <TableCell className="border-l-2 border-green-100 px-2 text-right text-green-700">
                  ${formatCurrency(fee.amount)}
                </TableCell>
                <TableCell className="text-muted-foreground px-2 text-right">-</TableCell>
                <TableCell className="border-l-2 border-gray-200 px-2 text-right font-bold">
                  ${formatCurrency(fee.amount)}
                </TableCell>
                <TableCell className="px-2 text-center">
                  <EnabledBadge enabled={true} />
                </TableCell>
              </TableRow>
            ))}

            {breakdown.platformCredits?.enabled && (breakdown.platformCredits.total ?? 0) < 0 && (
              <TableRow className="border-l-4 border-emerald-500 bg-emerald-50/30 hover:bg-emerald-50/50">
                <TableCell className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{t('feeBreakdown.platformCredit')}</span>
                    <span className="text-muted-foreground text-xs">
                      {t('feeBreakdown.tags.credit')}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground px-2 text-right font-medium">-</TableCell>
                <TableCell className="text-muted-foreground px-2 text-right font-medium">-</TableCell>
                <TableCell className="text-muted-foreground border-l-2 border-blue-100 px-2 text-right">-</TableCell>
                <TableCell className="text-muted-foreground px-2 text-right">-</TableCell>
                <TableCell className="border-l-2 border-green-100 px-2 text-right text-emerald-700">
                  -${formatCurrency(Math.abs(breakdown.platformCredits.total ?? 0))}
                </TableCell>
                <TableCell className="text-muted-foreground px-2 text-right">-</TableCell>
                <TableCell className="border-l-2 border-gray-200 px-2 text-right font-bold text-emerald-700">
                  -${formatCurrency(Math.abs(breakdown.platformCredits.total ?? 0))}
                </TableCell>
                <TableCell className="px-2 text-center">
                  <EnabledBadge enabled={true} />
                </TableCell>
              </TableRow>
            )}

            <TableRow className="from-muted to-muted/50 bg-gradient-to-r font-bold">
              <TableCell className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <span className="text-md">{t('feeBreakdown.totals.label')}</span>
                </div>
              </TableCell>
              <TableCell colSpan={4}></TableCell>
              <TableCell className="border-l-2 border-green-100 px-2 text-right">
                <div className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.totals.fixed')}</div>
                <div className="text-green-700">${formatCurrency(calculateTotalFixed() * 100)}</div>
              </TableCell>
              <TableCell className="px-2 text-right">
                <div className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.totals.percentage')}</div>
                <div className="text-green-700">${formatCurrency(calculateTotalPercentage() * 100)}</div>
              </TableCell>
              <TableCell className="border-l-2 border-gray-300 px-2 text-right">
                <div className="text-muted-foreground text-xs font-normal">{t('feeBreakdown.totals.combined')}</div>
                <div className="text-md">{formatMoney(totalFees, 'USD')}</div>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </>
  );
}
